import DaumPostcode, { Address } from "react-daum-postcode";
import { FormBlock } from "../formBlock/FormBlock";
import InputBox from "../inputBox/InputBox";
import { ButtonBox } from "../../button/Button";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import styles from "./postCode.module.scss";

type AddressKeys = "zipcode" | "basicAddress" | "detailAddress";

export const PostCode = ({
  updateAddress,
  resultObj,
  checkValidation = true,
  initialValue,
  customSearchHandler,
  postCodeModalOpen,
  addressInputs = { zipcode: true, basicAddress: true, detailAddress: true },
}: {
  updateAddress?: (address: {
    zipcodeChange: boolean;
    zipcode: string;
    basicAddress?: string;
    basicAddressChange: boolean;
    detailAddress?: string;
    detailAddressChange: boolean;
    all?: Address | null;
  }) => void;
  checkValidation?: boolean;
  resultObj?: {
    zipcode?: {
      result: "error" | "success" | "";
      message: string;
    };
    basicAddress?: {
      result: "error" | "success" | "";
      message: string;
    };
    detailAddress?: {
      result: "error" | "success" | "";
      message: string;
    };
  };
  initialValue?: {
    zipcode?: string;
    basicAddress?: string;
    detailAddress?: string;
  };
  customSearchHandler?: () => void;
  postCodeModalOpen?: boolean;
  addressInputs?: { [K in AddressKeys]?: boolean };
}) => {
  // 우편번호 모달 열기
  const [postModalIsOpen, setPostModalIsOpen] = useState(false);

  // 주소 데이터
  const addressData = useRef<Address | null>(null);

  // 우편번호, 기본주소, 상세주소
  const [zipcode, setZipcode] = useState("");
  const [basicAddress, setBasicAddress] = useState("");
  const [detailAddress, setDetailAddress] = useState("");

  // 상세주소 input ref
  const detailInputRef = useRef<HTMLInputElement | null>(null);

  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (postCodeModalOpen !== undefined && postCodeModalOpen !== null)
      setPostModalIsOpen(postCodeModalOpen);
  }, [postCodeModalOpen]);

  const initialValueMemo = useMemo(() => {
    return {
      zipcode: initialValue?.zipcode || "",
      basicAddress: initialValue?.basicAddress || "",
      detailAddress: initialValue?.detailAddress || "",
    };
  }, [initialValue?.zipcode, initialValue?.basicAddress, initialValue?.detailAddress]);

  const isTyping = useRef(false);
  useEffect(() => {
    if (!isTyping?.current) {
      if (
        addressInputs?.zipcode &&
        initialValueMemo?.zipcode !== null &&
        initialValueMemo?.zipcode !== updateDataRef.current?.zipcode
      ) {
        setZipcode(initialValueMemo.zipcode);
      }
      if (
        addressInputs?.basicAddress &&
        initialValueMemo?.basicAddress !== null &&
        initialValueMemo?.basicAddress !== updateDataRef.current?.basicAddress
      ) {
        setBasicAddress(initialValueMemo.basicAddress);
      }
      if (
        addressInputs?.detailAddress &&
        initialValueMemo?.detailAddress !== null &&
        initialValueMemo?.detailAddress !== updateDataRef.current?.detailAddress
      ) {
        setDetailAddress(initialValueMemo.detailAddress);
      }
    }
  }, [initialValueMemo]);

  // 입력값 변하면 업데이트
  const updateDataRef = useRef<{
    zipcodeChange: boolean;
    basicAddressChange: boolean;
    detailAddressChange: boolean;
    zipcode: string;
    basicAddress?: string;
    detailAddress?: string;
    all?: Address | null;
  }>({
    zipcodeChange: false,
    basicAddressChange: false,
    detailAddressChange: false,
    zipcode: "",
    basicAddress: "",
    detailAddress: "",
    all: null,
  });

  useEffect(() => {
    updateDataRef.current = {
      zipcodeChange: !!(updateDataRef?.current?.zipcode !== zipcode),
      basicAddressChange: !!(updateDataRef?.current?.basicAddress !== basicAddress),
      detailAddressChange: !!(updateDataRef?.current?.detailAddress !== detailAddress),
      zipcode,
      basicAddress,
      detailAddress,
      all: addressData.current,
    };

    try {
      updateAddress && updateAddress(updateDataRef.current);
    } catch (error) {
      console.log(error);
    }

    addressData.current = null;
  }, [zipcode, basicAddress, detailAddress]);

  const [defaultHeight, setDefaultHeight] = useState(0);

  const viewportResizeHandler = useCallback(() => {
    const currentVisualViewport = Number(window.visualViewport?.height);
    if (!modalRef?.current) return;
    if (Math.abs(defaultHeight - currentVisualViewport) >= 100) {
      modalRef.current.classList.add("resize");
    } else {
      modalRef.current.classList.remove("resize");
    }
  }, [defaultHeight, modalRef?.current]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window?.visualViewport) return;
    setDefaultHeight(window.outerHeight);
    window.visualViewport.addEventListener("resize", viewportResizeHandler);
    window.visualViewport.addEventListener("scroll", viewportResizeHandler);

    return () => {
      if (typeof window === "undefined") return;
      if (!window?.visualViewport) return;
      window.visualViewport.removeEventListener("resize", viewportResizeHandler);
      window.visualViewport.removeEventListener("scroll", viewportResizeHandler);
    };
  }, [window]);

  useEffect(() => {
    if (modalRef?.current) {
      modalRef.current.classList.remove("resize");
    }
  }, [postModalIsOpen]);

  return (
    <>
      {addressInputs["zipcode"] && (
        <FormBlock
          title="우편 번호"
          content={
            <InputBox
              type="fakeText"
              placeholder="주소 찾기"
              buttonComponent={
                <ButtonBox
                  size={"medium"}
                  theme={"red0"}
                  text={"우편 번호"}
                  onClick={() => {
                    customSearchHandler ? customSearchHandler() : setPostModalIsOpen(true);
                  }}
                />
              }
              value={zipcode}
              showHelpTextError={checkValidation ? true : false}
              showErrorBorder={checkValidation ? true : false}
              showHelpTextSuccess={checkValidation ? true : false}
              showSuccessBorder={checkValidation ? true : false}
              resultObj={checkValidation && resultObj?.zipcode}
              onClick={() => {
                customSearchHandler ? customSearchHandler() : setPostModalIsOpen(true);
              }}
            />
          }
        />
      )}
      {addressInputs["basicAddress"] && (
        <FormBlock
          title={"주소"}
          content={
            <InputBox
              type="fakeText"
              placeholder="주소를 입력해주세요."
              readOnly={true}
              value={basicAddress}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setBasicAddress(e.target.value);
              }}
              showHelpTextError={checkValidation ? true : false}
              showErrorBorder={checkValidation ? true : false}
              showHelpTextSuccess={checkValidation ? true : false}
              showSuccessBorder={checkValidation ? true : false}
              resultObj={checkValidation && resultObj?.basicAddress}
              onClick={() => {
                customSearchHandler ? customSearchHandler() : setPostModalIsOpen(true);
              }}
            />
          }
        />
      )}
      {addressInputs["detailAddress"] && (
        <FormBlock
          title={"상세 주소"}
          content={
            <InputBox
              ref={detailInputRef}
              placeholder="상세 주소를 입력해주세요."
              value={detailAddress}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                isTyping.current = true;
                setDetailAddress(e.target.value);
              }}
              onBlur={() => {
                isTyping.current = false;
              }}
              showHelpTextError={checkValidation ? true : false}
              showErrorBorder={checkValidation ? true : false}
              showHelpTextSuccess={checkValidation ? true : false}
              showSuccessBorder={checkValidation ? true : false}
              resultObj={checkValidation && resultObj?.detailAddress}
              maxLength={30}
            />
          }
        />
      )}
      <div className={styles["postcode-modal-wrap"]}>
        <div
          className={`${styles["postcode-overlay"]} ${postModalIsOpen ? "open" : ""}`}
          onClick={() => {
            setPostModalIsOpen(false);
          }}
        ></div>
        <div className={`${styles["postcode-modal"]} ${postModalIsOpen ? "open" : ""}`}>
          <div className={styles["postcode-modal-content"]} ref={modalRef}>
            {postModalIsOpen && (
              <DaumPostcode
                style={{
                  position: "absolute",
                  top: "0",
                  left: "0",
                  width: "100%",
                  height: "100%",
                  overflow: "scroll",
                }}
                onSearch={() => {
                  if (!modalRef?.current) return;
                  modalRef.current.classList.remove("resize");
                }}
                onComplete={(address: Address) => {
                  addressData.current = address;
                  setPostModalIsOpen(false);
                  setZipcode(address.zonecode);
                  setBasicAddress(address.roadAddress);
                  detailInputRef.current?.focus();
                  if (modalRef?.current) {
                    modalRef.current.classList.remove("resize");
                  }
                }}
                autoClose={false}
                submitMode={false}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
